import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { IconFileText } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import TemplateSetTable from '../../../components/templates/TemplateSetTable';
import GenerateDocDrawer from '../../../components/user/GenerateDocDrawer';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import templateSetService from '../../../services/templateSetService';
import LimitReachedModal from '../../../components/common/LimitReachedModal';
import LowCreditBanner from '../../../components/common/LowCreditBanner';

export default function TemplatesBrowser() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { selectedOfficeSpace, officeSpaces } = useOfficeSpaceStore();
    const { user } = useAuthStore();

    const [generateDrawerOpened, setGenerateDrawerOpened] = useState(false);
    const [selectedTemplateSet, setSelectedTemplateSet] = useState(null);
    const [loadingTemplateSetId, setLoadingTemplateSetId] = useState(null);
    const [limitModalOpened, setLimitModalOpened] = useState(false);

    // Load template set when slug is in URL (for direct URL access)
    useEffect(() => {
        const loadTemplateSetForGenerate = async () => {
            if (slug && !selectedTemplateSet) {
                setLoadingTemplateSetId(slug);
                try {
                    const response = await templateSetService.getTemplateSetById(slug);
                    const set = response.data;
                    // Free sets bypass the credit gate — the set must be loaded first to
                    // know its isFree flag, so this runs after the fetch (not before).
                    if (!set?.isFree && (user?.credit ?? 0) <= 0) {
                        setLimitModalOpened(true);
                        navigate('/template-sets');
                        return;
                    }
                    setSelectedTemplateSet(set);
                    setGenerateDrawerOpened(true);
                } catch (error) {
                    notifications.show({
                        title: t('common.error'),
                        message: t('templateSets.failedToLoad'),
                        color: 'red'
                    });
                    navigate('/template-sets');
                } finally {
                    setLoadingTemplateSetId(null);
                }
            }
        };

        loadTemplateSetForGenerate();
    }, [slug, navigate, selectedTemplateSet]);

    // Handle generate action
    const handleGenerate = (templateSet) => {
        if (!selectedOfficeSpace) {
            notifications.show({
                title: t('common.error'),
                message: t('officeSpace.noWorkspaceSelected'),
                color: 'red'
            });
            return;
        }

        // Block if no credits — unless the set is free (free sets skip deduction).
        if (!templateSet?.isFree && (user?.credit ?? 0) <= 0) {
            setLimitModalOpened(true);
            return;
        }

        setSelectedTemplateSet(templateSet);
        setGenerateDrawerOpened(true);
        navigate(`/template-sets/generate/${templateSet.slug ?? templateSet.id}`);
    };

    // Handle drawer close
    const handleDrawerClose = () => {
        setGenerateDrawerOpened(false);
        setSelectedTemplateSet(null);
        navigate('/template-sets');
    };

    const handleGenerateSuccess = (generatedDocs) => {
        setGenerateDrawerOpened(false);
        setSelectedTemplateSet(null);
        navigate('/documents');

        notifications.show({
            title: t('common.success'),
            message: t('documents.documentsGeneratedSuccessfully'),
            color: 'green'
        });
    };

    return (
        <div className="max-w-container mx-auto py-8" style={{ fontFamily: "'Hind Siliguri', 'Noto Sans Bengali', sans-serif" }}>
            {user?.role === 'OWNER' && (
                <LowCreditBanner credit={user?.credit ?? 0} className="mb-5" />
            )}
            <div className="flex items-center justify-between gap-3 mb-5">
                <h1 className="text-2xl font-bold tracking-[-0.01em] text-zinc-800 dark:text-zinc-100">
                    {t('templateSets.browseTemplateSets')}
                </h1>
                <Link
                    to="/documents?tab=documents"
                    className="inline-flex items-center gap-[7px] px-[15px] py-[9px] text-[13px] font-semibold border border-primary/20 bg-primary/10 text-primary rounded-[9px] hover:bg-primary/15 transition-colors"
                >
                    <IconFileText size={15} />
                    {t('common.myDocuments') || 'My Documents'}
                </Link>
            </div>

            {/* Template Set Table Component */}
            <TemplateSetTable
                isAdmin={false}
                showActions={true}
                showPinning={true}
                onGenerate={handleGenerate}
                loadingTemplateSetId={loadingTemplateSetId}
                disableGenerate={!selectedOfficeSpace}
            />

            {/* Generate Document Drawer */}
            {generateDrawerOpened && (
                <GenerateDocDrawer
                    opened={generateDrawerOpened}
                    onClose={handleDrawerClose}
                    templateSet={selectedTemplateSet}
                    onSuccess={handleGenerateSuccess}
                />
            )}

            {/* No credit modal */}
            <LimitReachedModal
                opened={limitModalOpened}
                onClose={() => setLimitModalOpened(false)}
            />
        </div>
    );
}
